'use client';

import { FC } from 'react';
import FadeIn from '@/components/FadeIn/FadeIn';
import { ComparisonData, ChooseReason } from '../types';

interface ChooseSectionProps {
  data: ComparisonData;
}

const TasksIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" />
    <line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const PhoneIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const SignalsIcon: FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
  </svg>
);

const getIcon = (iconType: ChooseReason['icon'], className?: string) => {
  switch (iconType) {
    case 'tasks':
      return <TasksIcon className={className} />;
    case 'phone':
      return <PhoneIcon className={className} />;
    case 'signals':
      return <SignalsIcon className={className} />;
    default:
      return <TasksIcon className={className} />;
  }
};

const ChooseSection: FC<ChooseSectionProps> = ({ data }) => {
  const { chooseReasons } = data;

  return (
    <section className="py-16 md:py-24">
      <div
        className="container-fluid px-4 md:px-7.5 mx-auto"
        style={{ maxWidth: 'clamp(1200px, 80vw, 1400px)' }}
      >
        <FadeIn variant="up" delay={0.1}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
            {/* MarketBetter Column */}
            <div>
              <h3
                className="font-medium mb-6"
                style={{
                  fontSize: 'clamp(1.25rem, 2vw, 1.75rem)',
                }}
              >
                Choose{' '}
                <span style={{ color: '#094FFE' }}>marketbetter.ai</span> if
              </h3>

              {/* Green progress indicator */}
              <div className="relative mb-8">
                <div className="h-1 bg-green-400 rounded-full w-full" />
                <div
                  className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-green-400 rounded-full"
                  style={{ transform: 'translate(50%, -50%)' }}
                />
              </div>

              {/* Reasons */}
              <ul className="space-y-6">
                {chooseReasons.marketbetter.map((reason, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-slate-500 mt-0.5">
                      {getIcon(reason.icon, 'w-5 h-5')}
                    </span>
                    <span
                      className="text-slate-700"
                      style={{ fontSize: 'clamp(0.9375rem, 1.25vw, 1.125rem)' }}
                    >
                      {reason.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Competitor Column */}
            <div>
              <h3
                className="font-medium text-slate-600 mb-6"
                style={{
                  fontSize: 'clamp(1.25rem, 2vw, 1.75rem)',
                }}
              >
                Choose {data.competitor.name} if
              </h3>

              {/* Gray wavy indicator */}
              <div className="relative mb-8">
                <svg
                  className="w-full h-2"
                  viewBox="0 0 400 8"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 4 Q 25 0, 50 4 T 100 4 T 150 4 T 200 4 T 250 4 T 300 4 T 350 4 T 400 4"
                    fill="none"
                    stroke="#E2E8F0"
                    strokeWidth="2"
                  />
                </svg>
              </div>

              {/* Reasons */}
              <ul className="space-y-6">
                {chooseReasons.competitor.map((reason, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <span className="text-slate-400 mt-0.5">
                      {getIcon(reason.icon, 'w-5 h-5')}
                    </span>
                    <span
                      className="text-slate-500"
                      style={{ fontSize: 'clamp(0.9375rem, 1.25vw, 1.125rem)' }}
                    >
                      {reason.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default ChooseSection;
